import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  addDoc,
  query, 
  where, 
  orderBy,
  getDocs,
  onSnapshot,
  writeBatch,
  enableNetwork,
  disableNetwork,
  connectFirestoreEmulator,
  serverTimestamp,
  Unsubscribe
} from 'firebase/firestore'
import { User } from 'firebase/auth'
import { db } from '@/lib/firebase'
import {
  Driver,
  MergedDriver,
  UserProfile,
  Upload,
  UploadStaging,
  DriverUploadStaging
} from '@/types'
import { FirebaseConnectionService } from './firebaseConnectionService'

// Encryption utilities for PII protection
class EncryptionService {
  private static key: string = 'salary-calc-key-2024'
  
  // Simple encryption for PII fields - in production, use proper encryption libraries
  static encrypt(text: string): string {
    if (!text) return ''
    try {
      // Base64 encoding with salt for basic obfuscation
      // TODO: Replace with proper AES encryption in production
      const encoded = btoa(text + ':' + this.key)
      return encoded
    } catch (error) {
      console.error('Encryption failed:', error)
      return text
    }
  }
  
  static decrypt(encryptedText: string): string {
    if (!encryptedText) return ''
    try {
      const decoded = atob(encryptedText)
      const [text] = decoded.split(':' + this.key)
      return text || ''
    } catch (error) {
      console.error('Decryption failed:', error)
      return encryptedText
    }
  }
}


// Main Firestore service with user isolation
export class FirestoreService {
  private userId: string
  private user: User
  private connectionService: FirebaseConnectionService
  
  constructor(user: User) {
    this.user = user
    this.userId = user.uid
    this.connectionService = FirebaseConnectionService.getInstance()
  }
  
  // Helper to get user-scoped collection references
  private getUserCollection(collectionName: string) {
    return collection(db, 'users', this.userId, collectionName)
  }
  
  private getUserDoc(collectionName: string, docId: string) {
    return doc(db, 'users', this.userId, collectionName, docId)
  }

  // Helper to remove undefined values, empty objects, and empty arrays from objects before saving to Firestore
  private sanitizeForFirestore(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj
    }

    if (Array.isArray(obj)) {
      const sanitizedArray = obj.map(item => this.sanitizeForFirestore(item)).filter(item => item !== null && item !== undefined)
      // Always return arrays, even if empty (some fields like normalizedData need to be preserved)
      return sanitizedArray
    }

    const sanitized: any = {}
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined && value !== null && value !== '') {
        const sanitizedValue = this.sanitizeForFirestore(value)

        // Skip empty objects and arrays
        if (sanitizedValue !== undefined && sanitizedValue !== '') {
          // For objects, check if it's empty after sanitization
          if (typeof sanitizedValue === 'object' && !Array.isArray(sanitizedValue) && Object.keys(sanitizedValue).length === 0) {
            // Preserve structural fields for DriverUpdateRow schema even when empty
            // These fields are part of the data structure and should not be removed
            const preservedStructuralFields = ['identifiers', 'profileData', 'conflicts']
            if (!preservedStructuralFields.includes(key)) {
              continue // Skip other empty objects
            }
          }
          sanitized[key] = sanitizedValue
        }
      }
    }

    // Always return the sanitized object, even if empty (for top-level objects)
    return sanitized
  }
  
  
  // DRIVER OPERATIONS
  // TODO: Implement new driver creation logic here
  
  // MERGED DRIVER OPERATIONS
  // TODO: Implement new merged driver logic here
  
  
  // UPLOAD OPERATIONS
  
  async createUpload(upload: Omit<Upload, 'id'>): Promise<string> {
    const sanitizedUpload = this.sanitizeForFirestore(upload)

    const finalData = {
      ...sanitizedUpload,
      uploadedAt: serverTimestamp()
    }

    const docRef = await addDoc(this.getUserCollection('uploads'), finalData)
    return docRef.id
  }

  async updateUpload(uploadId: string, updates: Partial<Upload>): Promise<void> {
    await updateDoc(this.getUserDoc('uploads', uploadId), updates)
  }

  async getUploads(): Promise<Upload[]> {
    const querySnapshot = await getDocs(
      query(
        this.getUserCollection('uploads'),
        orderBy('uploadedAt', 'desc')
      )
    )
    
    const uploads: Upload[] = []
    querySnapshot.forEach((doc) => {
      const data = doc.data()
      uploads.push({
        id: doc.id,
        ...data,
        uploadedAt: data.uploadedAt?.toDate() || new Date(),
        aiProcessingStartedAt: data.aiProcessingStartedAt?.toDate?.() || data.aiProcessingStartedAt,
        aiProcessingCompletedAt: data.aiProcessingCompletedAt?.toDate?.() || data.aiProcessingCompletedAt
      } as Upload)
    })
    
    return uploads
  }

  async getUpload(uploadId: string): Promise<Upload | null> {
    const docSnap = await getDoc(this.getUserDoc('uploads', uploadId))
    if (!docSnap.exists()) return null
    
    const data = docSnap.data()
    return {
      id: docSnap.id,
      ...data,
      uploadedAt: data.uploadedAt?.toDate() || new Date(),
      aiProcessingStartedAt: data.aiProcessingStartedAt?.toDate?.() || data.aiProcessingStartedAt,
      aiProcessingCompletedAt: data.aiProcessingCompletedAt?.toDate?.() || data.aiProcessingCompletedAt
    } as Upload
  }

  async deleteUpload(uploadId: string): Promise<void> {
    await deleteDoc(this.getUserDoc('uploads', uploadId))
  }

  async getUploadsByPeriod(period: string): Promise<Upload[]> {
    const querySnapshot = await getDocs(
      query(
        this.getUserCollection('uploads'),
        where('period', '==', period),
        orderBy('uploadedAt', 'desc')
      )
    )

    const uploads: Upload[] = []
    querySnapshot.forEach((doc) => {
      const data = doc.data()
      uploads.push({
        id: doc.id,
        ...data,
        uploadedAt: data.uploadedAt?.toDate() || new Date(),
        aiProcessingStartedAt: data.aiProcessingStartedAt?.toDate?.() || data.aiProcessingStartedAt,
        aiProcessingCompletedAt: data.aiProcessingCompletedAt?.toDate?.() || data.aiProcessingCompletedAt
      } as Upload)
    })

    return uploads
  }

  async deleteEarningsByUploadId(uploadSessionId: string): Promise<number> {
    // Get all earnings for this upload session (without isDeleted filter to avoid index issues)
    const q = query(
      this.getUserCollection('earnings'),
      where('uploadSessionId', '==', uploadSessionId)
    )

    const querySnapshot = await getDocs(q)

    if (querySnapshot.empty) {
      return 0 // No earnings to delete
    }

    // Filter out already deleted earnings and count them
    const nonDeletedDocs = querySnapshot.docs.filter(docSnap => {
      const data = docSnap.data()
      return !data.isDeleted
    })

    if (nonDeletedDocs.length === 0) {
      return 0
    }

    // Use batch operation for efficient bulk updates
    const batch = writeBatch(db)
    const now = serverTimestamp()

    nonDeletedDocs.forEach(docSnap => {
      const docRef = this.getUserDoc('earnings', docSnap.id)
      batch.update(docRef, {
        isDeleted: true,
        updatedAt: now
      })
    })

    // Commit the batch
    await batch.commit()

    return nonDeletedDocs.length
  }

  // UPLOAD STAGING OPERATIONS

  async createUploadStaging(staging: Omit<UploadStaging, 'id'>): Promise<string> {
    const sanitizedStaging = this.sanitizeForFirestore(staging)

    const finalData = {
      ...sanitizedStaging,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }

    const docRef = await addDoc(this.getUserCollection('uploadStaging'), finalData)
    return docRef.id
  }

  async updateUploadStaging(stagingId: string, updates: Partial<UploadStaging>): Promise<void> {
    const sanitizedUpdates = this.sanitizeForFirestore(updates)
    await updateDoc(this.getUserDoc('uploadStaging', stagingId), {
      ...sanitizedUpdates,
      updatedAt: serverTimestamp()
    })
  }

  async getUploadStaging(stagingId: string): Promise<UploadStaging | null> {
    const docSnap = await getDoc(this.getUserDoc('uploadStaging', stagingId))
    if (!docSnap.exists()) return null

    const data = docSnap.data()
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as UploadStaging
  }

  async getUploadStagingByUploadId(uploadId: string): Promise<UploadStaging | null> {
    const querySnapshot = await getDocs(
      query(
        this.getUserCollection('uploadStaging'),
        where('uploadId', '==', uploadId),
        orderBy('createdAt', 'desc')
      )
    )

    if (querySnapshot.empty) return null

    // Get the most recent staging for this upload
    const docSnap = querySnapshot.docs[0]
    const data = docSnap.data()
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as UploadStaging
  }

  async deleteUploadStaging(stagingId: string): Promise<void> {
    await deleteDoc(this.getUserDoc('uploadStaging', stagingId))
  }

  async listUploadStagings(): Promise<UploadStaging[]> {
    const querySnapshot = await getDocs(
      query(
        this.getUserCollection('uploadStaging'),
        orderBy('createdAt', 'desc')
      )
    )

    const stagings: UploadStaging[] = []
    querySnapshot.forEach((doc) => {
      const data = doc.data()
      stagings.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as UploadStaging)
    })

    return stagings
  }

  // DRIVER UPLOAD STAGING OPERATIONS

  async createDriverUploadStaging(
    staging: Omit<DriverUploadStaging, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const sanitizedStaging = this.sanitizeForFirestore(staging)

    const finalData = {
      ...sanitizedStaging,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }

    const docRef = await addDoc(this.getUserCollection('driver_uploads_staging'), finalData)
    return docRef.id
  }

  async updateDriverUploadStaging(
    stagingId: string,
    updates: Partial<DriverUploadStaging>
  ): Promise<void> {
    const sanitizedUpdates = this.sanitizeForFirestore(updates)
    await updateDoc(this.getUserDoc('driver_uploads_staging', stagingId), {
      ...sanitizedUpdates,
      updatedAt: serverTimestamp()
    })
  }

  async getDriverUploadStaging(stagingId: string): Promise<DriverUploadStaging | null> {
    const docSnap = await getDoc(this.getUserDoc('driver_uploads_staging', stagingId))
    if (!docSnap.exists()) return null

    const data = docSnap.data()
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as DriverUploadStaging
  }

  async getDriverUploadStagingByUploadId(uploadId: string): Promise<DriverUploadStaging | null> {
    const querySnapshot = await getDocs(
      query(
        this.getUserCollection('driver_uploads_staging'),
        where('uploadId', '==', uploadId),
        orderBy('createdAt', 'desc')
      )
    )

    if (querySnapshot.empty) return null

    const docSnap = querySnapshot.docs[0]
    const data = docSnap.data()
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as DriverUploadStaging
  }

  async deleteDriverUploadStaging(stagingId: string): Promise<void> {
    await deleteDoc(this.getUserDoc('driver_uploads_staging', stagingId))
  }

  // SETTINGS OPERATIONS
  
  async getSettings(): Promise<any> {
    const docSnap = await getDoc(this.getUserDoc('settings', 'config'))
    if (!docSnap.exists()) return null
    return docSnap.data()
  }

  async saveSettings(settings: any): Promise<void> {
    await setDoc(this.getUserDoc('settings', 'config'), {
      ...settings,
      updatedAt: serverTimestamp()
    })
  }
  
  // DATA EXPORT OPERATIONS
  
  async exportUserData(): Promise<any> {
    const settings = await this.getSettings()

    return {
      exportedAt: new Date().toISOString(),
      user: {
        uid: this.userId,
        email: this.user.email,
        displayName: this.user.displayName
      },
      data: {
        settings
      }
    }
  }
  
  
  // DATA DELETION
  
  async requestDataDeletion(): Promise<void> {
    const deletionRequest = {
      userId: this.userId,
      userEmail: this.user.email,
      requestedAt: serverTimestamp(),
      status: 'pending',
      dataTypes: ['drivers', 'merged_drivers', 'settings']
    }
    
    await addDoc(collection(db, 'deletion_requests'), deletionRequest)
  }
  
  // BATCH OPERATIONS
  // TODO: Implement new batch operations for drivers here
  
  // REAL-TIME SUBSCRIPTIONS  
  // TODO: Implement new subscriptions for drivers here
  
  // OFFLINE SUPPORT
  
  async enableOfflineSupport(): Promise<void> {
    try {
      await enableNetwork(db)
    } catch (error) {
      console.error('Failed to enable offline support:', error)
    }
  }
  
  async disableOfflineSupport(): Promise<void> {
    try {
      await disableNetwork(db)
    } catch (error) {
      console.error('Failed to disable offline support:', error)
    }
  }
  
  // CLEAR ALL USER DATA (for migration or testing)
  
  async clearAllUserData(): Promise<void> {
    const batch = writeBatch(db)
    
    // Get all user collections
    const collections = ['settings', 'periods']
    
    for (const collectionName of collections) {
      const snapshot = await getDocs(this.getUserCollection(collectionName))
      snapshot.forEach((doc) => {
        batch.delete(doc.ref)
      })
    }
    
    await batch.commit()
  }
}

// Factory function to create FirestoreService instance
export const createFirestoreService = (user: User): FirestoreService => {
  return new FirestoreService(user)
}

// Utility functions for migration and setup
export const FirestoreUtils = {
  // Enable Firestore offline persistence
  enableOfflinePersistence: async () => {
    try {
      // Note: This should be called before any other Firestore operations
      // and only once per app session
      console.log('Firestore offline persistence enabled')
    } catch (error) {
      console.error('Failed to enable offline persistence:', error)
    }
  },
  
  // Connect to Firestore emulator for development
  connectToEmulator: (host: string = 'localhost', port: number = 8080) => {
    try {
      connectFirestoreEmulator(db, host, port)
      console.log(`Connected to Firestore emulator at ${host}:${port}`)
    } catch (error) {
      console.error('Failed to connect to Firestore emulator:', error)
    }
  }
}
